import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useImmer } from "use-immer";

import { GraphConfig, LineData } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { LineMetricData } from "@src/types/analytics";

import { useGetTooltipProps } from "../../hooks/use-get-tooltip-props";
import { useGraphValueFormatter } from "../../hooks/use-graph-value-formatter";
import { InteractiveChartLegend } from "../../legends/InteractiveChartLegend";
import { METRIC_TO_DATA_TYPE } from "../../sitewideAnalytics/utils";
import { AxisTick } from "../chartComponents/AxisTick";
import { generateDistinctGraphColors } from "../utils";

interface CustomCursorProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
}

const CustomCursor = (props: CustomCursorProps) => {
  const theme = useTheme();
  const { x, y, width, height } = props;
  if (!height || !x || !y || !width) return;

  return (
    <line
      x1={x + width / 2}
      y1={y}
      x2={x + width / 2}
      y2={y + height}
      stroke={theme.igPrimitives.gray[900]}
      strokeWidth={1}
    />
  );
};

interface UnitQuantityByMetricChartProps {
  config: GraphConfig;
  dateFormat?: string;
  filterMethod?: string;
  groupMetricData: LineMetricData[];
  legends: LineData[];
  syncId?: string;
  graphHeight?: number;
}

export const UnitQuantityByMetricChart = ({
  config,
  dateFormat,
  filterMethod,
  groupMetricData,
  legends,
  syncId,
  graphHeight,
}: UnitQuantityByMetricChartProps) => {
  const theme = useTheme();
  const graphValueFormatter = useGraphValueFormatter();

  const isPercentageMetric = useMemo(
    () => filterMethod?.startsWith("pct_") ?? false,
    [filterMethod]
  );

  const dataType = useMemo(
    () => [filterMethod ? METRIC_TO_DATA_TYPE[filterMethod] : "number"],
    [filterMethod]
  );

  const graphColors = useMemo(() => {
    return generateDistinctGraphColors(legends.length);
  }, [legends]);

  const [baseFocusGroups, setBaseFocusGroups] = useImmer<string[]>([]);

  const tooltipProps = useGetTooltipProps({
    colors: generateDistinctGraphColors(legends.length),
    dataTypes: dataType,
    legends: legends.filter((line) => !baseFocusGroups.includes(line.id)),
    useZeroInsteadOfNA: true,
  });

  const handleLegendClick = (dataKey: string) => {
    if (baseFocusGroups.includes(dataKey)) {
      setBaseFocusGroups(baseFocusGroups.filter((el) => el !== dataKey));
    } else {
      setBaseFocusGroups((prev) => [...prev, dataKey]);
    }
  };

  return (
    <ResponsiveContainer width="100%" height={graphHeight ?? 400}>
      <BarChart
        data={groupMetricData}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        stackOffset={isPercentageMetric ? "expand" : undefined} // Help normalize data
        syncId={syncId}
      >
        <CartesianGrid
          style={{ stroke: theme.igBorders.core.disabled }}
          vertical={false}
        />

        <XAxis
          axisLine={{ stroke: theme.igBorders.core.disabled }}
          dataKey={"xAxisKey"}
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
              }}
              position={"insideBottom"}
              offset={-5}
              textAnchor={"middle"}
            />
          }
          tick={(props) => (
            <AxisTick
              config={config}
              dateFormat={dateFormat}
              graphValueFormatter={graphValueFormatter}
              groupMetricData={groupMetricData}
              {...props}
            />
          )}
          tickLine={false}
        />

        <YAxis
          axisLine={false}
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
                marginTop: "10px",
                textAnchor: "middle",
              }}
              angle={270}
              position={"insideLeft"}
              offset={10}
              textAnchor={"middle"}
            />
          }
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          tickFormatter={(value: any) =>
            graphValueFormatter({
              isAxisTick: true,
              value,
              configDataTypes: [
                filterMethod ? METRIC_TO_DATA_TYPE[filterMethod] : "number",
              ],
            })!
          }
          tickLine={false}
        />

        <Tooltip {...tooltipProps} cursor={<CustomCursor />} />

        {config.showLegend && legends.length > 1 && (
          <Legend
            content={
              <InteractiveChartLegend
                onLegendClick={handleLegendClick}
                focusGroups={baseFocusGroups}
                lines={legends}
                colors={graphColors}
              />
            }
            wrapperStyle={{
              paddingTop: 24,
            }}
          />
        )}

        {legends.map((legend, index) => (
          <Bar
            dataKey={legend.id}
            fill={graphColors[index]}
            hide={baseFocusGroups.includes(legend.id)}
            key={index}
            stackId="a"
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
